import { useState, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { CheckCircle, ChevronLeft, ChevronRight } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "../ui/Button";
import { Card, CardContent } from "../ui/Card";
import { Badge } from "../ui/Badge";
import type { ConsentFormValues } from "../../lib/consent-schema";
import { ZodError, z } from "zod";
import {
  DataProtectionSection,
  CodeOfConductSection,
  PersonalDetailsSection,
  MedicalSection,
  PhotoConsentSection,
} from "./SharedConsentFields";

interface ConsentFormStepperProps {
  participantName: string;
  eventRequirements: {
    requiresConsentForm: boolean;
    requiresPhotoConsent: boolean;
  };
  eventNames?: string[];
  initialData?: Partial<ConsentFormValues>;
  onStepSave?: (data: Partial<ConsentFormValues>) => Promise<void>;
  onComplete: (data: ConsentFormValues) => void;
  onCancel: () => void;
}

const getSteps = (
  requiresConsentForm: boolean,
  requiresPhotoConsent: boolean,
) => {
  const steps = [];

  // Data processing consent is always required
  steps.push({
    id: "dataProcessing",
    title: "Consents",
    description: "Required for all events",
  });

  if (requiresConsentForm) {
    steps.push(
      {
        id: "personal",
        title: "Personal Details",
        description: "Basic information and emergency contact",
      },
      {
        id: "medical",
        title: "Medical & Emergency",
        description: "Health conditions and emergency treatment consent",
      },
    );
  }

  if (requiresPhotoConsent) {
    steps.push({
      id: "photoConsent",
      title: "Photo Consent",
      description: "Required for events allowing photography",
    });
  }

  return steps;
};

export const ConsentFormStepper = ({
  participantName,
  eventRequirements,
  eventNames,
  initialData,
  onStepSave,
  onComplete,
  onCancel,
}: ConsentFormStepperProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [errorUpdateKey, setErrorUpdateKey] = useState(0);
  const [isSavingStep, setIsSavingStep] = useState(false);
  const steps = useMemo(
    () =>
      getSteps(
        eventRequirements.requiresConsentForm,
        eventRequirements.requiresPhotoConsent,
      ),
    [eventRequirements],
  );

  const form = useForm<ConsentFormValues>({
    // Don't use zodResolver - we'll validate manually per step
    // resolver: zodResolver(strictConsentFormSchema),
    mode: "onTouched", // Only validate on blur/touch
    reValidateMode: "onChange",
    defaultValues: {
      ...initialData,
      // Ensure booleans are initialized if missing
      dataProcessingConsent: initialData?.dataProcessingConsent ?? false,
      consentFormCodeOfConductAccepted:
        initialData?.consentFormCodeOfConductAccepted ?? false,
      emergencyTreatmentConsent:
        initialData?.emergencyTreatmentConsent ?? false,
      isUnder18: !!initialData?.consentFormParticipantDateOfBirth,
      hasMedication: !!initialData?.consentFormMedicationDetails,
      hasInjuries: !!initialData?.consentFormInjuriesDetails,
      hasDietaryRequirements:
        !!initialData?.consentFormDietaryRequirementsDetails,
      hasDisability: !!initialData?.consentFormDisabilityDetails,
    },
  });

  const {
    register,
    watch,
    getValues,
    getFieldState,
    setError,
    clearErrors,
    formState: { errors },
  } = form;

  const contentRef = useRef<HTMLDivElement>(null);

  const handleNext = async () => {
    const currentStepId = steps[currentStep].id;
    let fieldsToValidate: (keyof ConsentFormValues)[] = [];

    if (currentStepId === "dataProcessing") {
      fieldsToValidate = [
        "dataProcessingConsent",
        "consentFormCodeOfConductAccepted",
      ];
    } else if (currentStepId === "personal") {
      fieldsToValidate = [
        "firstName",
        "lastName",
        "consentFormParticipantPronouns",
        "consentFormParticipantAddress",
        "isUnder18",
        "consentFormEmergencyContact1Name",
        "consentFormEmergencyContact1Phone",
        "consentFormEmergencyContact1Relationship",
        "consentFormEmergencyContact2Name",
        "consentFormEmergencyContact2Phone",
        "consentFormEmergencyContact2Relationship",
      ];
      if (watch("isUnder18")) {
        fieldsToValidate.push(
          "consentFormParticipantDateOfBirth",
          "consentFormParentCarerFirstName",
          "consentFormParentCarerSurname",
          "consentFormParentCarerAddress",
          "consentFormParentCarerRelationship",
          "consentFormParentCarerContactNumber",
        );
      }
    } else if (currentStepId === "medical") {
      fieldsToValidate = ["emergencyTreatmentConsent"];
      if (watch("hasMedication"))
        fieldsToValidate.push("consentFormMedicationDetails");
      if (watch("hasInjuries"))
        fieldsToValidate.push("consentFormInjuriesDetails");
      if (watch("hasDietaryRequirements"))
        fieldsToValidate.push("consentFormDietaryRequirementsDetails");
      if (watch("hasDisability"))
        fieldsToValidate.push("consentFormDisabilityDetails");
    } else if (currentStepId === "photoConsent") {
      fieldsToValidate = ["photoConsent"];
    }

    // Validate only the fields for the current step manually
    // We can't use trigger() because the zodResolver validates the entire schema
    // Instead, we'll manually validate just the fields we care about
    const currentValues = getValues();
    const fieldsToValidateSet = new Set(fieldsToValidate);
    
    // Clear any existing errors for fields we're about to validate
    // This ensures we only show errors for the current step
    for (const field of fieldsToValidate) {
      if (errors[field]) {
        // Clear the error - we'll re-validate it
        setError(field, { type: "manual", message: "" }, { shouldFocus: false });
      }
    }
    
    let validationErrors: ZodError | null = null;
    let isValid = true;
    
    // Manually validate each field for this step
    // This avoids the full schema validation that checks fields from other steps
    const stepErrors: Array<{ field: keyof ConsentFormValues; message: string }> = [];
    
    // Validate checkboxes (must be true)
    if (fieldsToValidate.includes("dataProcessingConsent")) {
      if (currentValues.dataProcessingConsent !== true) {
        stepErrors.push({ field: "dataProcessingConsent", message: "Data processing consent must be accepted" });
        isValid = false;
      }
    }
    
    if (fieldsToValidate.includes("consentFormCodeOfConductAccepted")) {
      if (currentValues.consentFormCodeOfConductAccepted !== true) {
        stepErrors.push({ field: "consentFormCodeOfConductAccepted", message: "Code of conduct must be accepted" });
        isValid = false;
      }
    }
    
    if (fieldsToValidate.includes("emergencyTreatmentConsent")) {
      if (currentValues.emergencyTreatmentConsent !== true) {
        stepErrors.push({ field: "emergencyTreatmentConsent", message: "Emergency treatment consent must be accepted" });
        isValid = false;
      }
    }
    
    // Validate required strings
    const checkRequiredString = (field: keyof ConsentFormValues, label: string) => {
      const value = currentValues[field];
      if (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "")
      ) {
        stepErrors.push({ field, message: `${label} is required` });
        isValid = false;
      }
    };
    
    if (fieldsToValidate.includes("consentFormParticipantPronouns")) {
      checkRequiredString("consentFormParticipantPronouns", "Participant pronouns");
    }
    
    if (fieldsToValidate.includes("consentFormParticipantAddress")) {
      checkRequiredString("consentFormParticipantAddress", "Participant address");
    }
    
    if (fieldsToValidate.includes("consentFormEmergencyContact1Name")) {
      checkRequiredString("consentFormEmergencyContact1Name", "Emergency contact 1 name");
    }
    
    if (fieldsToValidate.includes("consentFormEmergencyContact1Relationship")) {
      checkRequiredString("consentFormEmergencyContact1Relationship", "Emergency contact 1 relationship");
    }
    
    if (fieldsToValidate.includes("consentFormEmergencyContact1Phone")) {
      checkRequiredString("consentFormEmergencyContact1Phone", "Emergency contact 1 phone");
    }
    
    if (fieldsToValidate.includes("consentFormEmergencyContact2Name")) {
      checkRequiredString("consentFormEmergencyContact2Name", "Emergency contact 2 name");
    }
    
    if (fieldsToValidate.includes("consentFormEmergencyContact2Relationship")) {
      checkRequiredString("consentFormEmergencyContact2Relationship", "Emergency contact 2 relationship");
    }
    
    if (fieldsToValidate.includes("consentFormEmergencyContact2Phone")) {
      checkRequiredString("consentFormEmergencyContact2Phone", "Emergency contact 2 phone");
    }
    
    // Validate conditional fields for under-18
    if (watch("isUnder18") && fieldsToValidate.includes("isUnder18")) {
      if (fieldsToValidate.includes("consentFormParticipantDateOfBirth")) {
        checkRequiredString("consentFormParticipantDateOfBirth", "Participant date of birth");
      }
      if (fieldsToValidate.includes("consentFormParentCarerFirstName")) {
        checkRequiredString("consentFormParentCarerFirstName", "Parent/carer first name");
      }
      if (fieldsToValidate.includes("consentFormParentCarerSurname")) {
        checkRequiredString("consentFormParentCarerSurname", "Parent/carer surname");
      }
      if (fieldsToValidate.includes("consentFormParentCarerAddress")) {
        checkRequiredString("consentFormParentCarerAddress", "Parent/carer address");
      }
      if (fieldsToValidate.includes("consentFormParentCarerRelationship")) {
        checkRequiredString("consentFormParentCarerRelationship", "Parent/carer relationship");
      }
      if (fieldsToValidate.includes("consentFormParentCarerContactNumber")) {
        checkRequiredString("consentFormParentCarerContactNumber", "Parent/carer contact number");
      }
    }
    
    // Validate conditional medical fields
    if (watch("hasMedication") && fieldsToValidate.includes("consentFormMedicationDetails")) {
      checkRequiredString("consentFormMedicationDetails", "Medication details");
    }
    
    if (watch("hasInjuries") && fieldsToValidate.includes("consentFormInjuriesDetails")) {
      checkRequiredString("consentFormInjuriesDetails", "Injury details");
    }
    
    if (watch("hasDietaryRequirements") && fieldsToValidate.includes("consentFormDietaryRequirementsDetails")) {
      checkRequiredString("consentFormDietaryRequirementsDetails", "Dietary requirement details");
    }
    
    if (watch("hasDisability") && fieldsToValidate.includes("consentFormDisabilityDetails")) {
      checkRequiredString("consentFormDisabilityDetails", "Disability details");
    }
    
    // Validate photoConsent - it's optional but if the step exists, a value should be selected
    if (fieldsToValidate.includes("photoConsent")) {
      const photoConsentValue = currentValues.photoConsent;
      // photoConsent can be "true", "false", true, false, null, or undefined
      // If it's null or undefined, that's invalid (user must make a choice)
      if (photoConsentValue === null || photoConsentValue === undefined) {
        stepErrors.push({ field: "photoConsent", message: "Photo consent selection is required" });
        isValid = false;
      }
    }
    
    // Clear errors for fields we're about to validate (to remove stale errors)
    for (const field of fieldsToValidate) {
      clearErrors(field);
    }
    
    // Set errors for fields that failed validation
    for (const error of stepErrors) {
      setError(error.field, {
        type: "validation",
        message: error.message,
      }, { shouldFocus: false });
    }
    
    if (!isValid) {
      validationErrors = new ZodError(stepErrors.map(e => ({
        code: z.ZodIssueCode.custom,
        message: e.message,
        path: [e.field],
      })));
    }
    
    
    // Wait for errors to propagate and form state to update
    await new Promise((resolve) => setTimeout(resolve, 100));
    
    // Force re-render to ensure errors are displayed
    setErrorUpdateKey((prev) => prev + 1);
    
    // Check if validation passed - only check if we found errors in this step
    // Don't check formState.errors as it might have stale errors from previous steps
    const hasStepErrors = stepErrors.length > 0;
    
    if (isValid && !validationErrors && !hasStepErrors) {
      if (currentStep < steps.length - 1) {
        if (onStepSave) {
          setIsSavingStep(true);
          try {
            await onStepSave(getValues());
          } catch {
            return;
          } finally {
            setIsSavingStep(false);
          }
        }
        setCurrentStep(currentStep + 1);
      } else {
        onComplete(getValues());
      }
    } else {
      // Validation failed - always show toast and errors
      
      const firstErrorField = fieldsToValidate.find(
        (field) => {
          const fieldState = getFieldState(field);
          return fieldState.error || errors[field];
        },
      ) || (validationErrors?.issues.find(issue => 
        fieldsToValidateSet.has(issue.path[0] as keyof ConsentFormValues)
      )?.path[0] as string | undefined);
      
      // ALWAYS show toast when validation fails - use requestAnimationFrame to ensure DOM is ready
      window.requestAnimationFrame(() => {
        toast.error("Please fix the highlighted fields before continuing", {
          duration: 4000,
          position: "top-right",
        });
      });
      
      // Scroll to first error field after a short delay to ensure DOM is updated
      setTimeout(() => {
        if (firstErrorField) {
          const errorElement = document.querySelector(
            `[name="${firstErrorField}"]`,
          );
          if (errorElement) {
            // TypeScript knows this is an Element, which has scrollIntoView
            (errorElement as { scrollIntoView: (options?: { behavior?: string; block?: string }) => void; focus?: () => void }).scrollIntoView({ behavior: "smooth", block: "center" });
            // Focus if the element supports it (input, textarea, select, etc.)
            if ("focus" in errorElement && typeof errorElement.focus === "function") {
              errorElement.focus();
            }
          } else if (contentRef.current) {
            // Fallback: scroll to top of content area
            contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
          }
        } else if (contentRef.current) {
          // Fallback: scroll to top of content area
          contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 150);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    const currentStepId = steps[currentStep]?.id;

    switch (currentStepId) {
      case "dataProcessing":
        return (
          <div className="space-y-6">
            <DataProtectionSection
              register={register}
              errors={errors}
              name={participantName}
            />
            <CodeOfConductSection
              register={register}
              errors={errors}
              name={participantName}
            />
          </div>
        );

      case "personal":
        return (
          <div className="space-y-6">
            <PersonalDetailsSection
              register={register}
              errors={errors}
              watch={watch}
              name={participantName}
            />
          </div>
        );

      case "medical":
        return (
          <div className="space-y-6">
            <MedicalSection
              register={register}
              errors={errors}
              watch={watch}
              name={participantName}
            />
          </div>
        );

      case "photoConsent":
        return (
          <PhotoConsentSection
            register={register}
            errors={errors}
            name={participantName}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Card className="w-full flex flex-col h-full min-h-0 border-none shadow-none">
      <CardContent ref={contentRef} className="flex-1 overflow-y-auto min-h-0 pt-0">
        <div className="mb-6 pb-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">
                Consent Form - {participantName}
              </h2>
              {eventNames && eventNames.length > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  Attending {eventNames.length} event
                  {eventNames.length !== 1 ? "s" : ""}: {eventNames.join(", ")}
                </p>
              )}
              <p className="text-gray-600 mt-1">
                Step {currentStep + 1} of {steps.length}:{" "}
                {steps[currentStep].description}
              </p>
            </div>
            <Badge variant="info" className="text-center justify-center">
              {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
            </Badge>
          </div>
        </div>

        <div className="mb-8">
          <div className="flex items-center justify-center">
            {steps.map((step, index) => {
              const isCompleted = index < currentStep;
              const isCurrent = index === currentStep;

              return (
                <div key={step.id} className="flex items-center">
                  <div
                    className={`
                      flex items-center justify-center w-10 h-10 rounded-full border-2 relative z-10
                      ${
                        isCompleted
                          ? "bg-green-500 border-green-500 text-white"
                          : isCurrent
                            ? "border-primary-500 text-primary-500"
                            : "border-gray-300 text-gray-400"
                      }
                    `}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <span className="text-sm font-bold">{index + 1}</span>
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div className="w-12 h-0.5 bg-gray-300 mx-2" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mb-8" key={errorUpdateKey}>{renderStepContent()}</div>
      </CardContent>

      <div className="border-t px-6 py-4 flex justify-between items-center bg-white">
        <Button
          variant="outline"
          onClick={currentStep === 0 ? onCancel : handlePrevious}
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          {currentStep === 0 ? "Cancel" : "Previous"}
        </Button>

        <Button onClick={handleNext} disabled={isSavingStep}>
          {currentStep === steps.length - 1
            ? "Complete Form"
            : isSavingStep
              ? "Saving..."
              : "Next"}
          {currentStep < steps.length - 1 && (
            <ChevronRight className="w-4 h-4 ml-2" />
          )}
        </Button>
      </div>
    </Card>
  );
};
